// Pure decision logic for the receptionist capability panel.
// Kept free of React so the rules that decide what the operator sees (and what a click sends)
// can be tested without a browser.

export const AUDIO_TYPES = ["audio", "ambient_audio"];

/** Roles permitted by the server to inspect the infrastructure capability surface.
 * Receptionists have their own scheduling/check-in view; trainers do not have
 * capability.surface.view and must never trigger this API from their console.
 */
export function canViewCapabilitySurface(user) {
  const roles = Array.isArray(user?.roles)
    ? user.roles.map((role) => String(role).toUpperCase())
    : [];
  return roles.some((role) => ["PLATFORM_ADMIN", "ADMIN", "OWNER", "MANAGER"].includes(role));
}

export function isAudioCapability(capability) {
  return AUDIO_TYPES.includes(capability?.type);
}

export function availablePlaylists(capability) {
  return Array.isArray(capability?.playlists) ? capability.playlists.filter((item) => item?.available) : [];
}

/** The persisted/manual selection shown by the dropdown. Never invent a fallback. */
export function resolvePlaylistSelection(capability, picked) {
  if (typeof picked === "string" && picked) return picked;
  return typeof capability?.selected_playlist_id === "string"
    ? capability.selected_playlist_id
    : "";
}

/** Operator-facing status for one capability row: { label, tone, hint }. */
export function describeCapabilityStatus(capability, controlsStatus = "ready") {
  if (controlsStatus === "loading") return { label: "Checking…", tone: "muted", hint: "" };
  if (controlsStatus === "error") return { label: "Unavailable", tone: "warn", hint: "" };

  if (capability.connection === "NOT_CONNECTED") return { label: "Not connected", tone: "muted", hint: "" };
  if (capability.connection === "UNKNOWN") return { label: "Status unknown", tone: "muted", hint: "" };

  if (isAudioCapability(capability)) {
    if (capability.state === "PLAYING") {
      const playlist = capability.playlists?.find((item) => item.id === capability.playlist_id);
      return { label: playlist ? `Playing · ${playlist.label}` : "Playing", tone: "ok", hint: "" };
    }
    if (capability.state === "STOPPED") return { label: "Stopped", tone: "muted", hint: "" };
    return { label: "Ready", tone: "muted", hint: "" };
  }

  if (capability.state === "ON" || capability.state === "OFF") {
    return {
      label: capability.state === "ON" ? "On" : "Off",
      tone: capability.state === "ON" ? "ok" : "muted",
      hint: capability.state_source === "last_command" ? "Last command sent from HACaMS" : "",
    };
  }
  if (capability.connection === "CONNECTED") return { label: "State unknown", tone: "muted", hint: "No command sent yet" };
  return { label: "Available", tone: "muted", hint: "" };
}

/** Which of a capability's actions should be offered at all. */
export function visibleActions(capability, controlsStatus = "ready") {
  if (controlsStatus !== "ready") return [];
  if (capability.connection === "NOT_CONNECTED") return [];
  return Array.isArray(capability.actions)
    ? capability.actions.filter((action) => action?.execution !== "selection")
    : [];
}

/** Why a row has no buttons, or "" when it has some / needs no explanation. */
export function emptyActionsNote(capability, controlsStatus = "ready") {
  if (controlsStatus === "loading") return "";
  if (controlsStatus === "error") return "Controls could not be loaded.";
  if (capability.connection === "NOT_CONNECTED") return "Not connected to a physical device yet.";
  if (visibleActions(capability, controlsStatus).length === 0 && capability.type !== "scheduling") {
    return "No operator actions are declared.";
  }
  return "";
}
